import * as React from "react";
import {
  CYCLE_PHASE_ORDER,
  cn,
  getPhasesForCycleDay,
  type CyclePhaseKey,
  type PhaseSpan,
} from "@nfp-tracker/utils";

export interface PhaseBandOption {
  key: CyclePhaseKey;
  label: string;
  /** Short label for tight spaces (hover cards, aria labels). */
  shortLabel: string;
  /** Band color, and the swatch color in the legend. */
  colorClass: string;
}

/**
 * Phase bands are thin rules above the day square, not another background wash — the two
 * washes (bleeding intensity, fertile signal) stay the only full-cell states, so adding
 * phase context can't blot out or be confused with a day's own observation.
 *
 * Colors reuse the palette each phase already has elsewhere: red for bleeding, green for
 * the fertile wash, violet for the monitor PEAK chip. Amber is deliberately unused — it's
 * the active symptom-filter ring.
 */
export const PHASE_BAND_OPTIONS: PhaseBandOption[] = [
  { key: "menses", label: "Menses", shortLabel: "Menses", colorClass: "bg-red-600" },
  {
    key: "preOvulatory",
    label: "Pre-ovulatory",
    shortLabel: "Pre-ovulatory",
    colorClass: "bg-slate-400",
  },
  {
    key: "fertile",
    label: "Fertile window",
    shortLabel: "Fertile",
    colorClass: "bg-green-600",
  },
  { key: "peak", label: "Peak day", shortLabel: "Peak", colorClass: "bg-violet-700" },
  {
    // Starts where the fertile window closes, not at Peak + 1 — the three days after Peak
    // are still inside the window.
    key: "postPeak",
    label: "Post-peak infertile",
    shortLabel: "Post-peak",
    colorClass: "bg-indigo-500",
  },
];

/** Fertile window and post-peak on by default; the rest opt-in, to keep the grid readable. */
export const DEFAULT_VISIBLE_PHASE_BANDS: CyclePhaseKey[] = ["fertile", "postPeak"];

/** More than three stacked rules re-clutters the thing the bands exist to declutter. */
export const MAX_VISIBLE_PHASE_BANDS = 3;

const PHASE_OPTION_BY_KEY = new Map(
  PHASE_BAND_OPTIONS.map((option) => [option.key, option])
);

export const getPhaseBandOption = (key: CyclePhaseKey) => PHASE_OPTION_BY_KEY.get(key);

/**
 * Bands for a single day, ordered by `CYCLE_PHASE_ORDER` and always drawn in that slot —
 * a band that changed rows between cycles would read as a different phase.
 */
export const resolveDayPhaseBands = (
  cycleDay: number,
  spans: PhaseSpan[],
  visiblePhases: CyclePhaseKey[]
): PhaseSpan[] => {
  const visible = CYCLE_PHASE_ORDER.filter((key) => visiblePhases.includes(key)).slice(
    0,
    MAX_VISIBLE_PHASE_BANDS
  );
  return getPhasesForCycleDay(cycleDay, spans).filter((span) => visible.includes(span.key));
};

/** Phase names covering a day, for aria labels and hover cards (the bands are decorative). */
export const describeDayPhases = (
  cycleDay: number,
  spans: PhaseSpan[],
  visiblePhases: CyclePhaseKey[]
): string[] =>
  resolveDayPhaseBands(cycleDay, spans, visiblePhases).map(
    (span) => getPhaseBandOption(span.key)?.label ?? span.key
  );

export interface PhaseBandStackProps {
  cycleDay: number;
  spans: PhaseSpan[];
  visiblePhases?: CyclePhaseKey[];
  className?: string;
}

/**
 * Decorative stack of 2px rules for one day. Rendered inside a full-day-width wrapper so
 * consecutive days' segments touch — the day squares themselves carry margin, which would
 * otherwise chop each band into disconnected dashes.
 *
 * Caps are rounded only at a span's first/last day, so a run reads as one continuous line
 * with defined ends. An `open` span (running cycle, or no Peak identified yet) is drawn at
 * reduced opacity with no closing cap, so it never implies a phase that has ended.
 */
export const PhaseBandStack = ({
  cycleDay,
  spans,
  visiblePhases = DEFAULT_VISIBLE_PHASE_BANDS,
  className,
}: PhaseBandStackProps) => {
  const bands = resolveDayPhaseBands(cycleDay, spans, visiblePhases);
  if (!bands.length) return null;

  return (
    <div
      aria-hidden="true"
      className={cn("pointer-events-none flex w-full flex-col gap-px", className)}
    >
      {bands.map((span) => {
        const option = getPhaseBandOption(span.key);
        if (!option) return null;
        const isFirstDay = cycleDay === span.startCd;
        const isLastDay = cycleDay === span.endCd;

        return (
          <span
            key={span.key}
            className={cn(
              "h-0.5 w-full",
              option.colorClass,
              span.open && "opacity-60",
              isFirstDay && "rounded-l-full",
              isLastDay && !span.open && "rounded-r-full"
            )}
          />
        );
      })}
    </div>
  );
};

export interface PhaseBandLegendProps {
  visiblePhases?: CyclePhaseKey[];
  onTogglePhase?: (key: CyclePhaseKey) => void;
}

/** Toggle row for the phase bands, sharing the chip treatment of the other legend rows. */
export const PhaseBandLegend = ({
  visiblePhases = DEFAULT_VISIBLE_PHASE_BANDS,
  onTogglePhase,
}: PhaseBandLegendProps) => {
  const atCap =
    visiblePhases.length >= MAX_VISIBLE_PHASE_BANDS;

  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px]">
      <span className="font-semibold text-muted-foreground">Phases</span>
      {PHASE_BAND_OPTIONS.map((option) => {
        const active = visiblePhases.includes(option.key);
        const disabled = !active && atCap;
        return (
          <button
            key={option.key}
            type="button"
            onClick={() => onTogglePhase?.(option.key)}
            aria-pressed={active}
            disabled={disabled}
            title={
              disabled
                ? `Turn one off first — up to ${MAX_VISIBLE_PHASE_BANDS} phases at a time`
                : undefined
            }
            className={cn(
              "inline-flex items-center gap-1 rounded border px-2 py-1 transition-colors",
              active
                ? "border-amber-500 bg-amber-500/20 text-amber-700"
                : "border-border/40 bg-muted/30 text-muted-foreground/50 hover:border-amber-400/60",
              disabled && "cursor-not-allowed opacity-50 hover:border-border/40"
            )}
          >
            <span className={cn("h-0.5 w-3 rounded-full", option.colorClass)} />
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
